import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { ConfigContext } from "../App";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { patchData, checkProductQuantity, priceFormatter, formatPrice } from "../functions/utils";
import emailjs from "emailjs-com";
import { useMediaQuery } from "react-responsive";
import { tokens } from "../CMS/client";
import ThankYou from "../components/Checkout/ThankYou";
import CheckoutItems from "../components/Checkout/CheckoutItems";
import Popup from "../components/Popup/Popup";
import Loading from "../components/Loading/Loading";
import config from "../configuration.json";
import { TranslateContext } from "../App";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, doc, runTransaction } from "firebase/firestore";
import {
  getDefaultShippingOption,
  getShippingOptions,
  getSupportedDestinationTypes,
  mapSpeedyOfficeToAddress,
  resolveLocalizedString,
} from "../functions/shipping";

function Checkout() {
  const language = useContext(TranslateContext).language;
  emailjs.init(tokens.emailjs_init);
  const emailjs_service_id = tokens.emailjs_service_id;
  const emailjs_template_id_client = tokens.emailjs_template_id_client;
  const emailjs_template_id_summary = tokens.emailjs_template_id_summary;
  const [popup, setPopup] = useState({ isOpen: false, title: "", body: "" });
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [destinationType, setDestinationType] = useState("home");
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [orderedItems, setOrderedItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [orderedTotal, setOrderedTotal] = useState(0);
  const [checkedOut, setCheckedOut] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const isMobile = useMediaQuery({ maxWidth: 1023 });
  const configuration = useContext(ConfigContext);
  const formRef = useRef();
  const shippingPrice = 0;
  const shippingOptions = useMemo(() => getShippingOptions(configuration), [configuration]);
  const selectedDeliveryLabel = resolveLocalizedString(selectedCarrier?.name, language) || selectedCarrier?.slug || "";
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!selectedCarrier && shippingOptions.length > 0) {
      const defaultOption = getDefaultShippingOption(shippingOptions);
      setSelectedCarrier(defaultOption);
      setDestinationType(getSupportedDestinationTypes(defaultOption)[0] || "home");
    }
  }, [shippingOptions, selectedCarrier]);

  /**
   * Handles form submission.
   * Prevents multiple submissions by immediately setting processing state.
   * @param {Object} data - The form data.
   */
  const handleFormSubmit = async (data) => {
    // Prevent multiple submissions - set processing state immediately
    if (isProcessing) {
      return;
    }
    setIsProcessing(true);

    const updatedUserDetails = {
      ...data,
      selectedDelivery: selectedDeliveryLabel,
      destinationType,
      selectedOffice,
    };
    setUserDetails(updatedUserDetails);

    const updatedCartItems = await checkProductAvailability(cartItems);
    if (!updatedCartItems) {
      // Reset processing state if availability check fails
      setIsProcessing(false);
      return;
    }

    // Await the checkout process before showing success
    const checkoutResult = await handleCheckout(data, updatedCartItems);

    // Hide loading state
    setIsProcessing(false);

    if (checkoutResult.success) {
      setCheckedOut(true);

      // Show warning if emails failed but order was saved
      if (!checkoutResult.emailsSent) {
        setPopup({
          isOpen: true,
          title: "Поръчката е запазена",
          body: `Поръчката Ви е запазена успешно, но възникна проблем с изпращането на имейл потвърждение. Моля, свържете се с нас за потвърждение на телефон: ${configuration?.businessInfo?.phone} или имейл: ${configuration?.businessInfo?.email}`,
        });
      }
    }

    // Dispatch a custom event after updating localStorage
    window.dispatchEvent(new CustomEvent("cartItemsUpdated"));
  };

  /**
   * Checks the availability of each product in the cart.
   * @param {Array} cartItems - The items in the cart.
   * @returns {Array} The updated cart items.
   */
  const checkProductAvailability = async (cartItems) => {
    let updatedCartItems = [...cartItems];
    for (let i = 0; i < updatedCartItems.length; i++) {
      const availableQuantity = await checkProductQuantity(updatedCartItems[i].id);

      if (availableQuantity < updatedCartItems[i].cartQuantity) {
        setPopup({
          isOpen: true,
          title: config.texts.oops[language],
          body: `${config.texts.selectedQuantity[language]} "${updatedCartItems[i].title}", ${config.texts.isMoreThanAvailable[language]} (${availableQuantity} ${config.texts.pcsLeft[language]}). ${config.texts.pleaseChangeQuantity[language]}`,
        });
        return null;
      }
    }
    return updatedCartItems;
  };

  /**
   * Saves the order to Firestore database.
   * @param {Object} data - The form data.
   * @param {Array} updatedCartItems - The updated cart items.
   * @param {number} orderedTotal - The total price of the order.
   */
  const saveOrderToFirestore = async (data, updatedCartItems, orderedTotal) => {
    try {
      // Generate sequential order number using transaction
      const orderNumber = await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, "counters", "orderCounter");
        const counterDoc = await transaction.get(counterRef);

        let nextOrderNumber = 1;
        if (counterDoc.exists()) {
          nextOrderNumber = counterDoc.data().currentNumber + 1;
        }

        transaction.set(counterRef, { currentNumber: nextOrderNumber }, { merge: true });
        return nextOrderNumber;
      });

      const officeMapped = mapSpeedyOfficeToAddress(selectedOffice);

      const orderData = {
        orderNumber: orderNumber, // Sequential order number (0000001, 0000002, etc.)
        customer: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phoneNumber,
          companyName: data.companyName || null,
          bulstat: data.bulstat || null,
          vatRegistration: data.vatRegistration || null,
          invoiceAddress: data.invoiceAddress || null,
          mol: data.mol || null,
        },
        shipping: {
          delivery: selectedDeliveryLabel,
          carrierSlug: selectedCarrier?.slug || null,
          carrierName: selectedDeliveryLabel,
          destinationType,
          city: data.city,
          address: data.address,
          postalCode: data.postalCode,
          office: selectedOffice
            ? {
                id: officeMapped.officeId,
                name: officeMapped.officeName,
                raw: selectedOffice,
              }
            : null,
        },
        items: updatedCartItems.map((item) => ({
          id: item.id,
          title: item.title,
          price: item.price,
          quantity: item.cartQuantity,
          image: item.image,
        })),
        total: parseFloat(orderedTotal) + Number(shippingPrice),
        shippingPrice: shippingPrice,
        additionalInfo: data.additionalInfo || null,
        status: "pending", // pending, completed
        notes: "", // admin notes
        createdAt: serverTimestamp(),
        completedAt: null,
      };

      const docRef = await addDoc(collection(db, "orders"), orderData);
      // console.log('Order saved to Firestore with ID: ', docRef.id, 'and order number:', orderNumber);
      return orderNumber.toString().padStart(7, "0"); // Return formatted order number
    } catch (error) {
      // console.error("Error saving order to Firestore: ", error);
      // Don't block the checkout process if Firestore fails
      return null;
    }
  };

  /**
   * Handles the checkout process.
   * @param {Object} data - The form data.
   * @param {Array} updatedCartItems - The updated cart items.
   * @returns {Promise<Object>} Result object with success and emailsSent status.
   */
  const handleCheckout = async (data, updatedCartItems) => {
    let emailsSent = false;

    try {
      // Update the cart items in the state and local storage
      setCartItems(updatedCartItems);
      localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));

      // Update the quantity of each ordered item (await all updates)
      const patchPromises = updatedCartItems.map((item) =>
        patchData(item.id, item.cartQuantity).catch((err) => {
          // console.error(`Failed to update quantity for product ${item.id}:`, err);
          // Don't throw - we still want to complete the order
        })
      );
      await Promise.all(patchPromises);

      // Clear the local storage from the cart items
      localStorage.removeItem("cartItems");

      // Calculate total before state update (to avoid stale state issues)
      const calculatedTotal = updatedCartItems.reduce((total, item) => total + item.price * item.cartQuantity, 0);

      // Save order to Firestore
      const orderId = await saveOrderToFirestore(data, updatedCartItems, calculatedTotal);

      // Set orderedItems (capture current cart before clearing)
      setOrderedItems([...updatedCartItems]);
      setOrderedTotal(calculatedTotal);

      // Clear the cart items from the state
      setCartItems([]);

      // Note: Removed external images from email to improve deliverability
      // Gmail often filters emails with external images from unfamiliar domains
      const orderedItemsSummary = updatedCartItems
        .map(
          (item) => `
      <tr>
        <td style="padding: 10px; text-align: left;">${
          item.title[language] ? item.title[language] : item.title
        }</td>
        <td style="padding: 10px; text-align: center;">${item.cartQuantity}</td>
        <td style="padding: 10px; text-align: right;">${formatPrice(
          item.price,
          language,
          config
        )}</td>
      </tr>
    `
        )
        .join("");

      const tableHTML = `
      <h2>Номер на поръчка: ${orderId}</h2>
      <table style="width: 100%; border-collapse: collapse;">
        <thead style="background-color: #f0f0f0;">
          <tr>
            <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Продукт</th>
            <th style="border: 1px solid #ddd; padding: 10px; text-align: center;">К-во</th>
            <th style="border: 1px solid #ddd; padding: 10px; text-align: right;">Ед. цена</th>
          </tr>
        </thead>
        <tbody>
          ${orderedItemsSummary}
        </tbody>
      </table>
    `;

      const officeMapped = mapSpeedyOfficeToAddress(selectedOffice);

      // Send an email to the client and the store owner
      const templateParams = {
        order_number: orderId,
        client_name: data.firstName,
        client_last_name: data.lastName,
        client_phone: data.phoneNumber,
        to_email: data.email,
        from_email: configuration.businessInfo.email,
        order_details: tableHTML,
        store_phone: configuration.businessInfo.phone,
        store_website: configuration.businessInfo.shop,
        reply_to: configuration.businessInfo.email,
        store_name: configuration.title,
        selected_delivery: selectedDeliveryLabel,
        destination_type: destinationType,
        office_name: officeMapped.officeName || "",
        office_id: officeMapped.officeId || "",
        additional_info: data.additionalInfo,
        address_details: {
          city: data.city,
          address: data.address,
          postalCode: data.postalCode,
        },
        corporate_info: {
          company_name: data.companyName,
          bulstat: data.bulstat,
          vat: data.vatRegistration,
          address: data.invoiceAddress,
          mol: data.mol,
        },
        shipping: {
          price: shippingPrice ? `${formatPrice(shippingPrice, language, config)}` : null,
          delivery_firm: selectedDeliveryLabel,
          destination_type: destinationType,
          office_name: officeMapped.officeName || "",
        },
        order_total: `${formatPrice(
          parseFloat(calculatedTotal) + Number(shippingPrice),
          language,
          config
        )}`,
      };

      // Validate email configuration before attempting to send
      if (!emailjs_service_id || !emailjs_template_id_client || !emailjs_template_id_summary) {
        // console.error("Email configuration is missing. Check environment variables.");
        // console.error("emailjs_service_id:", emailjs_service_id ? "SET" : "MISSING");
        // console.error("emailjs_template_id_client:", emailjs_template_id_client ? "SET" : "MISSING");
        // console.error("emailjs_template_id_summary:", emailjs_template_id_summary ? "SET" : "MISSING");
        // console.error("emailjs_init:", tokens.emailjs_init ? "SET" : "MISSING");
        return { success: true, emailsSent: false };
      }

      try {
        // console.log("Attempting to send order confirmation emails...");
        // console.log("Service ID:", emailjs_service_id);
        // console.log("Template ID (client):", emailjs_template_id_client);
        // console.log("Template ID (summary):", emailjs_template_id_summary);
        // console.log("Sending TO email:", data.email);
        // console.log("Sending FROM email:", configuration.businessInfo.email);
        
        // Send emails and await both promises
        const emailResults = await Promise.all([
          emailjs.send(emailjs_service_id, emailjs_template_id_client, templateParams),
          emailjs.send(emailjs_service_id, emailjs_template_id_summary, templateParams),
        ]);
        emailsSent = true;
        // console.log("Order confirmation emails sent successfully:", emailResults);
      } catch (emailError) {
        // console.error("Failed to send order confirmation emails:", emailError);
        // console.error("Error details:", JSON.stringify(emailError, null, 2));
        // Don't throw - order is already saved, we'll notify the user about email failure
      }

      return { success: true, emailsSent };
    } catch (error) {
      // console.error("Checkout process failed:", error);
      setPopup({
        isOpen: true,
        title: config.texts.oops[language],
        body:
          config.texts.checkoutError?.[language] ||
          "Възникна грешка при обработката на поръчката. Моля, опитайте отново или се свържете с нас.",
      });
      return { success: false, emailsSent: false };
    }
  };

  /**
   * Adds an item to the cart.
   * @param {string} itemId - The ID of the item to add.
   */
  const onAdd = (itemId) => {
    const cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
    const item = cartItems.find((item) => item.id === itemId);
    if (item) {
      item.cartQuantity += 1;
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      setCartItems(cartItems);
    }
    window.dispatchEvent(new Event("cartItemsUpdated"));
  };

  /**
   * Subtracts an item from the cart.
   * @param {string} itemId - The ID of the item to subtract.
   */
  const onSubtract = (itemId) => {
    let cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
    const itemIndex = cartItems.findIndex((item) => item.id === itemId);
    if (itemIndex !== -1) {
      if (cartItems[itemIndex].cartQuantity > 1) {
        // Create a new array with the updated item
        cartItems = cartItems.map((item) =>
          item.id === itemId ? { ...item, cartQuantity: item.cartQuantity - 1 } : item
        );
      } else {
        // Create a new array without the item
        cartItems = cartItems.filter((item) => item.id !== itemId);
      }
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      setCartItems(cartItems); // Update the local state
      window.dispatchEvent(new Event("cartItemsUpdated"));
    }
  };

  useEffect(() => {
    let total = 0;
    for (let item of cartItems) {
      total += item.price * item.cartQuantity;
    }
    if (cartItems.length > 0) {
      setOrderedTotal(total);
    }
    setTotalPrice(total);
  }, [cartItems]);

  useEffect(() => {
    const storedCartItems = localStorage.getItem("cartItems");
    if (storedCartItems) {
      const parsedItems = JSON.parse(storedCartItems);
      setCartItems(parsedItems);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>{configuration.title}</title>
      </Helmet>
      <>
        {isProcessing && <Loading />}
        {cartItems.length === 0 && !checkedOut && !isProcessing && (
          <div className="row">
            <div className="row-container">
              <h5 className="h5-title text-center w-full">{config.texts.cardEmpty[language]}</h5>
            </div>
          </div>
        )}
        {orderedItems.length > 0 && checkedOut && !isProcessing && (
          <ThankYou
            configuration={configuration}
            isMobile={isMobile}
            orderedItems={orderedItems}
            orderedTotal={orderedTotal}
            priceFormatter={priceFormatter}
            shippingPrice={shippingPrice}
            userDetails={userDetails}
            selectedDelivery={selectedDeliveryLabel}
            destinationType={destinationType}
            invoiceNeeded={userDetails.companyName}
          />
        )}
        {cartItems.length > 0 && !checkedOut && selectedCarrier && (
          <CheckoutItems
            cartItems={cartItems}
            onAdd={onAdd}
            onSubtract={onSubtract}
            totalPrice={totalPrice}
            shippingPrice={shippingPrice}
            handleFormSubmit={handleFormSubmit}
            handleSubmit={handleSubmit}
            formRef={formRef}
            register={register}
            setValue={setValue}
            errors={errors}
            isMobile={isMobile}
            shippingOptions={shippingOptions}
            selectedCarrier={selectedCarrier}
            setSelectedCarrier={setSelectedCarrier}
            destinationType={destinationType}
            setDestinationType={setDestinationType}
            selectedOffice={selectedOffice}
            setSelectedOffice={setSelectedOffice}
            selectedDelivery={selectedDeliveryLabel}
            isProcessing={isProcessing}
          />
        )}
        <Popup
          title={popup.title}
          body={popup.body}
          isOpen={popup.isOpen}
          setIsOpen={(isOpen) => setPopup((prev) => ({ ...prev, isOpen }))}
          configuration={configuration}
        />
      </>
    </>
  );
}
export default Checkout;
